// AccountCreateFlow.tsx

import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Typography,
  alpha,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import { CreateAccount } from './CreateAccount';
import { CheckoutForm } from './Checkout';
import { useTheme } from '../../contexts/ThemeContext';
import { rarityColors, amberColors, numenorColors } from '../../theme/themeConfig';

// Import Material UI Icons
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import WarningIcon from '@mui/icons-material/Warning';

const steps = [
  'Create Account',
  "Set up Payments (Optional — do it now for convenience or skip; you'll be prompted again at the end of your trial. You can go Home to log in anytime.)",
];

export const AccountCreateFlow = () => {
  const [setupStep, setSetupStep] = useState(1);
  const [accountId, setAccountId] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    if (location.pathname === '/signup') {
      setSetupStep(1);
    } else if (location.pathname === '/signup/payment') {
      setSetupStep(2);
    }
  }, [location.pathname, navigate]);

  const handleNextStep = () => {
    setSetupStep(prevStep => prevStep + 1);
    switch (setupStep + 1) {
      case 2:
        navigate('/signup/payment');
        break;
      default:
        break;
    }
  };

  // Background with blueprint image
  const backgroundStyle = darkMode
    ? {
        background: `
          linear-gradient(135deg, ${alpha('#111827', 0.85)} 0%, ${alpha('#1f2937', 0.85)} 50%, ${alpha('#111827', 0.85)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      }
    : {
        background: `
          linear-gradient(135deg, ${alpha(numenorColors[50], 0.78)} 0%, ${alpha(numenorColors[100], 0.78)} 50%, ${alpha(numenorColors[50], 0.78)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      };

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ width: 256, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: darkMode ? '#ffffff' : amberColors[600] }}
          >
            Unity Build Estimation
          </Typography>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            size="small"
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Chip
          label="Beta"
          size="small"
          sx={{
            bgcolor: rarityColors.epic,
            color: 'white',
            fontWeight: 600,
          }}
        />
      </Box>

      {/* Theme Toggle */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={toggleDarkMode} size="small">
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          <Typography variant="body2" sx={{ color: darkMode ? '#ffffff' : 'text.secondary' }}>
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, p: 2 }}>
        <Typography
          variant="overline"
          sx={{
            color: darkMode ? '#ffffff' : 'text.secondary',
            fontWeight: 600,
            mb: 2,
            display: 'block',
          }}
        >
          NAVIGATION
        </Typography>
        <List sx={{ p: 0 }}>
          <ListItem
            button
            component="a"
            href="/"
            sx={{
              borderRadius: 1,
              mb: 1,
              '&:hover': {
                bgcolor: alpha(amberColors[600], 0.15),
                '& .MuiListItemText-primary': {
                  color: darkMode ? '#ffffff' : 'inherit',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? '#ffffff' : amberColors[600],
                },
              },
            }}
          >
            <ListItemIcon>
              <HomeIcon sx={{ color: darkMode ? '#ffffff' : 'inherit' }} />
            </ListItemIcon>
            <ListItemText
              primary="Home"
              sx={{ '& .MuiTypography-root': { color: darkMode ? '#ffffff' : 'inherit' } }}
            />
          </ListItem>
          <ListItem
            sx={{
              borderRadius: 1,
              mb: 1,
              cursor: 'not-allowed',
              opacity: 0.6,
            }}
          >
            <ListItemIcon>
              <BusinessIcon sx={{ color: darkMode ? '#ffffff' : 'inherit' }} />
            </ListItemIcon>
            <ListItemText
              primary="The Foundry"
              sx={{ '& .MuiTypography-root': { color: darkMode ? '#ffffff' : 'inherit' } }}
            />
            <Chip
              label="Coming Soon!"
              size="small"
              sx={{
                bgcolor: rarityColors.rare,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
              }}
            />
          </ListItem>
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography
          variant="caption"
          sx={{ color: darkMode ? '#ffffff' : 'text.secondary', display: 'block', mb: 1 }}
        >
          © 2025 Unity Build Estimation
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <WarningIcon sx={{ fontSize: 16, color: rarityColors.epic, mr: 0.5 }} />
          <Typography variant="caption" sx={{ color: rarityColors.epic }}>
            beta development
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Sidebar */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 256,
            bgcolor: darkMode ? 'background.paper' : 'background.default',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main Layout with Blueprint Background */}
      <Box
        sx={{
          minHeight: '100vh',
          ...backgroundStyle,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Mobile Menu Button */}
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1000,
            display: { xs: 'block', md: 'none' },
          }}
        >
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              bgcolor: alpha(darkMode ? '#1a1a1a' : '#ffffff', 0.9),
              backdropFilter: 'blur(10px)',
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                bgcolor: alpha(darkMode ? '#2d2d2d' : '#f5f5f5', 0.9),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Desktop Sidebar */}
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            width: 256,
            zIndex: 900,
            display: { xs: 'none', md: 'block' },
            bgcolor: darkMode ? 'background.paper' : 'background.default',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          {sidebarContent}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            ml: { xs: 0, md: '256px' },
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
            <Box sx={{ width: '100%', mt: { xs: 2, md: 4 }, mb: 4 }}>
              <Stepper activeStep={setupStep - 1} alternativeLabel>
                {steps.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Box sx={{ my: 4 }}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <CreateAccount
                        accountId={accountId}
                        setAccountId={setAccountId}
                        onNextStep={handleNextStep}
                      />
                    }
                  />
                  <Route
                    path="payment"
                    element={<CheckoutForm accountId={accountId} isTrial={true} />}
                  />
                </Routes>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};
